<template>
  <Dropdown
    ref="dropdown"
    v-model="control.value"
    :options="options"
    :loading="control.customStyles?.loading || false"
    :disabled="control.disabled"
    :showClear="!!control.value"
    optionLabel="label"
    placeholder="Selecione"
    :panelStyle="{ width: panelWidthPx + 'px' }"
    :emptyMessage="'Nenhum resultado encontrado'"
    :class="
      control.valid === true
        ? 'w-full p-disabled-components'
        : 'w-full p-invalid p-disabled-components'
    "
    :virtualScrollerOptions="scrollerOptions"
    @before-show="onBeforeShow"
    @update:modelValue="onSelect"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur', $event)"
  >
    <!-- Busca própria no cabeçalho: o `filter` nativo do Dropdown roda o
         FilterService sobre o array local, que em modo lazy é quase todo
         `undefined`. Por isso ele fica desligado e a busca vai ao servidor. -->
    <template #header>
      <div class="lazy-dropdown-header">
        <InputText
          v-model="search"
          class="w-full"
          :placeholder="
            control.customStyles?.filterPlaceholder || 'Pesquisar...'
          "
          @update:model-value="onSearchInput"
        />
      </div>
    </template>

    <!-- Sempre montamos o rótulo a partir do próprio `control.value`. O rótulo
         padrão do Dropdown procura o valor dentro de `options`, que aqui está
         vazio até a primeira abertura — o valor já gravado no registro
         apareceria como "Selecione". -->
    <template #value="slot">
      <span v-if="labelOf(slot.value)">{{ labelOf(slot.value) }}</span>
      <span v-else>{{ slot.placeholder }}</span>
    </template>

    <template
      v-if="control.customStyles?.propetyFilter?.[0]"
      #option="slotProps"
    >
      <div class="flex flex-column">
        <span>{{
          slotProps.option?.value?.[control.customStyles?.propetyFilter[0]]
        }}</span>
        <span
          v-if="control.customStyles?.propetyFilter[1]"
          class="ml-2"
          style="font-size: 10px"
          >{{
            slotProps.option?.value?.[
              control.customStyles?.propetyFilter[1]
            ]
          }}</span
        >
      </div>
    </template>

    <!-- Renderizado no lugar dos itens da faixa que ainda está sendo buscada. -->
    <template #loader>
      <div class="lazy-dropdown-loader" :style="{ height: itemSize + 'px' }">
        <Skeleton width="60%" height="1rem" />
      </div>
    </template>

    <template #clearicon>
      <i
        class="p-icon p-dropdown-clear-icon pi pi-times"
        style="cursor: pointer"
        @click="clear"
      ></i>
    </template>

    <template #empty>
      <div style="padding: 0.5rem 1rem">
        <span class="p-dropdown-item-label" data-pc-section="itemlabel">
          {{ loading ? "Carregando..." : "Nenhum resultado encontrado" }}
        </span>
      </div>
    </template>
  </Dropdown>
</template>

<script>
/**
 * Dropdown com virtual scroll lazy (PrimeVue 3.47): mantém um array do
 * tamanho do total real com apenas as faixas visíveis preenchidas, buscadas
 * sob demanda. O campo opta por este modo declarando `customStyles.lazy`:
 *
 *   customStyles: {
 *     lazy: {
 *       // devolve { items, total } para a faixa pedida
 *       fetch: ({ first, last, search }) => Promise<{ items, total }>,
 *       itemSize: 38,   // opcional, altura da linha em px
 *       pageSize: 300,  // opcional, mínimo de itens por busca
 *     },
 *   }
 */
export default {
  name: "LazyDropdown",
  emits: ["focus", "blur", "change"],
  props: {
    control: { type: Object, required: true },
  },
  data() {
    return {
      options: [],
      total: 0,
      search: "",
      loading: false,
      loaded: false,
      panelWidthPx: 0,
    };
  },
  computed: {
    itemSize() {
      return this.control.customStyles?.lazy?.itemSize || 38;
    },
    pageSize() {
      return this.control.customStyles?.lazy?.pageSize || 50;
    },
    /**
     * Com `total <= pageSize` o reload já trouxe tudo; manter o lazy só
     * adicionaria o controle de faixas e o loader, sem ganho nenhum.
     */
    isLazy() {
      return this.total > this.pageSize;
    },
    scrollerOptions() {
      const options = { itemSize: this.itemSize };
      if (!this.isLazy) return options;
      return {
        ...options,
        lazy: true,
        showLoader: true,
        step: this.pageSize,
        loading: this.loading,
        onLazyLoad: this.onLazyLoad,
      };
    },
  },
  created() {
    let timer = null;
    this.onSearchInput = () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.reload(), 300);
    };
    this.clearSearchTimer = () => clearTimeout(timer);
  },
  beforeUnmount() {
    this.clearSearchTimer?.();
  },
  methods: {
    async onBeforeShow() {
      this.panelWidthPx = this.$refs.dropdown?.$el?.offsetWidth || 0;
      // Só busca na primeira abertura.
      if (!this.loaded) await this.reload();
    },
    async reload() {
      this.loading = true;
      try {
        const { items, total } = await this.fetch(0, this.pageSize);
        this.total = total;
        // O array dimensiona a barra de rolagem e marca o fim da lista.
        const next = new Array(total);
        this.fill(next, 0, items);
        this.options = next;
        this.loaded = true;
      } finally {
        this.loading = false;
      }
    },
    /** `first`/`last` já vêm alinhados a `step`: nada a arredondar aqui. */
    async onLazyLoad({ first, last }) {
      if (!this.loaded) return;
      const start = Math.max(first, 0);
      const end = Math.min(last, this.total);
      if (this.isRangeLoaded(start, end)) return;
      this.loading = true;
      try {
        const { items } = await this.fetch(start, end);
        // Escrita por índice: reativa no Vue 3, sem copiar o array inteiro.
        this.fill(this.options, start, items);
      } finally {
        this.loading = false;
      }
    },
    fetch(first, last) {
      return this.control.customStyles.lazy.fetch({
        first,
        last,
        search: this.search,
      });
    },
    fill(target, first, items) {
      items.forEach((item, i) => {
        target[first + i] = item;
      });
    },
    isRangeLoaded(first, last) {
      for (let i = first; i < last; i++) {
        if (this.options[i] === undefined) return false;
      }
      return true;
    },
    /** O `$separator` é a convenção do `propetyFilter`: o que vem depois só
     * serve para a busca, não é exibido. */
    labelOf(option) {
      const label = option?.label;
      return typeof label === "string" ? label.split("$separator")[0] : "";
    },
    onSelect(value) {
      this.control.value = value?.value ? value : "";
      this.$emit("change", value);
    },
    clear() {
      this.control.value = "";
      this.$emit("change", "");
    },
  },
};
</script>

<style>
.lazy-dropdown-header {
  padding: 0.5rem;
  border-bottom: 1px solid #dee2e6;
}
.lazy-dropdown-loader {
  display: flex;
  align-items: center;
  padding: 0 1rem;
}
</style>
